import type { Head } from '@unhead/vue'
import { getMediaById, normalizeMedia, type AttachmentModel } from '@/utils/media'

type RecordLike = Record<string, unknown>
type SeoHeadLink = {
  rel: 'alternate'
  key: string
  href: string
  hreflang: string
}

export type SeoSource = {
  id?: number
  slug?: string
  title?: string
  type?: string
  relativeUrl?: string
  media?: number
  excerpt?: unknown
  meta?: unknown
  seo?: unknown
  hero?: unknown
  translations?: unknown
  raw?: unknown
}

export type BuildSeoHeadOptions = {
  routePath: string
  locale?: string
  siteName: string
  siteOrigin?: string
  content?: SeoSource | null
  attachments?: AttachmentModel[]
  locations?: SeoSource[]
  defaults: {
    title: string
    description: string
    image?: string
  }
}
type SeoOgType =
  | 'article'
  | 'website'
  | 'book'
  | 'profile'
  | 'music.song'
  | 'music.album'
  | 'music.playlist'
  | 'music.radio_status'
  | 'video.movie'
  | 'video.episode'
  | 'video.tv_show'
  | 'video.other'

function asRecord(value: unknown): RecordLike | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordLike)
    : null
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

function normalizeText(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const text = stripHtml(value)
    return text || undefined
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const text = normalizeText(item)
      if (text) return text
    }
    return undefined
  }

  const record = asRecord(value)
  if (!record) return undefined

  return firstString(record, [
    ['rendered'],
    ['description'],
    ['text'],
    ['content'],
    ['title'],
    ['value'],
  ])
}

function getByPath(source: RecordLike, path: string[]) {
  let current: unknown = source

  for (const segment of path) {
    const record = asRecord(current)
    if (!record || !(segment in record)) return undefined
    current = record[segment]
  }

  return current
}

function firstValue(source: RecordLike | null, paths: string[][]) {
  if (!source) return undefined

  for (const path of paths) {
    const value = getByPath(source, path)
    if (value != null && value !== '') return value
  }

  return undefined
}

function firstString(source: RecordLike | null, paths: string[][]) {
  const value = firstValue(source, paths)
  return normalizeText(value)
}

function normalizeHtmlLang(locale?: string) {
  return locale?.toLowerCase().startsWith('en') ? 'en' : 'no'
}

function normalizeOgLocale(locale?: string) {
  return normalizeHtmlLang(locale) === 'en' ? 'en_US' : 'nb_NO'
}

function normalizeOgType(value: string | undefined, fallback: SeoOgType): SeoOgType {
  switch (value) {
    case 'article':
    case 'website':
    case 'book':
    case 'profile':
    case 'music.song':
    case 'music.album':
    case 'music.playlist':
    case 'music.radio_status':
    case 'video.movie':
    case 'video.episode':
    case 'video.tv_show':
    case 'video.other':
      return value
    default:
      return fallback
  }
}

function trimTrailingSlash(url: string) {
  if (url.length <= 1) return url
  return url.endsWith('/') ? url.slice(0, -1) : url
}

function buildCanonicalUrl(candidate: string | undefined, routePath: string, siteOrigin?: string) {
  const next = candidate || routePath || '/'

  try {
    const url = siteOrigin
      ? new URL(next, siteOrigin)
      : new URL(next)

    url.hash = ''
    url.search = ''

    if (url.pathname !== '/') {
      url.pathname = trimTrailingSlash(url.pathname)
    }

    return trimTrailingSlash(url.toString())
  } catch {
    const clean = next.replace(/[?#].*$/, '')
    if (clean === '/') return clean
    return trimTrailingSlash(clean)
  }
}

function buildAbsoluteUrl(candidate: unknown, siteOrigin?: string) {
  if (typeof candidate !== 'string') return undefined

  const next = candidate.trim() || '/'

  try {
    const url = siteOrigin
      ? new URL(next, siteOrigin)
      : new URL(next)

    url.hash = ''
    url.search = ''

    return url.toString()
  } catch {
    return undefined
  }
}

function preferSiteOrigin(candidate: string | undefined, siteOrigin?: string) {
  if (!candidate || !siteOrigin) return candidate

  try {
    const source = new URL(candidate, siteOrigin)
    const frontend = new URL(siteOrigin)

    source.protocol = frontend.protocol
    source.host = frontend.host

    return source.toString()
  } catch {
    return candidate
  }
}

function resolveAlternateLanguageLinks(
  translations: unknown,
  siteOrigin?: string,
): SeoHeadLink[] {
  const entries = asRecord(translations)
  if (!entries) return []

  const seen = new Set<string>()
  const links: SeoHeadLink[] = []

  Object.entries(entries).forEach(([fallbackLang, value]) => {
    const entry = asRecord(value)
    if (!entry || entry.exists === false) return

    const hreflang = firstString(entry, [['hreflang']]) || fallbackLang
    const relativeUrl = firstValue(entry, [['relativeUrl'], ['relative_url']])
    const absoluteUrl = buildAbsoluteUrl(firstValue(entry, [['link']]))
    const href =
      buildAbsoluteUrl(relativeUrl, siteOrigin) ||
      buildAbsoluteUrl(preferSiteOrigin(absoluteUrl, siteOrigin), siteOrigin)

    if (!hreflang || !href || seen.has(hreflang)) return

    seen.add(hreflang)
    links.push({
      rel: 'alternate',
      hreflang,
      href,
      key: `alternate-${hreflang}`,
    })
  })

  return links
}

function imageUrlFromUnknown(
  value: unknown,
  attachments: AttachmentModel[],
): string | undefined {
  if (typeof value === 'string') {
    const text = value.trim()
    return text || undefined
  }

  if (typeof value === 'number') {
    const media = normalizeMedia(getMediaById(attachments, value))
    return media?.type === 'image' ? media.url : undefined
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const image = imageUrlFromUnknown(item, attachments)
      if (image) return image
    }
    return undefined
  }

  const record = asRecord(value)
  if (!record) return undefined

  const direct = firstString(record, [
    ['url'],
    ['src'],
    ['source_url'],
    ['image_url'],
    ['og_image'],
    ['guid', 'rendered'],
    ['sizes', 'full', 'source_url'],
    ['sizes', 'large', 'source_url'],
    ['image', 'url'],
    ['image', 'source_url'],
    ['image', 'sizes', 'full', 'source_url'],
  ])

  if (direct) return direct

  const maybeId = firstValue(record, [
    ['id'],
    ['image', 'id'],
    ['attachment_id'],
    ['media_id'],
  ])

  if (typeof maybeId === 'number') {
    const media = normalizeMedia(getMediaById(attachments, maybeId))
    return media?.type === 'image' ? media.url : undefined
  }

  return undefined
}

function resolveImage(
  meta: RecordLike | null,
  attachments: AttachmentModel[],
  fallbackMediaId?: number,
  paths: string[][] = [],
) {
  const image = imageUrlFromUnknown(firstValue(meta, paths), attachments)
  if (image) return image

  if (typeof fallbackMediaId === 'number') {
    const media = normalizeMedia(getMediaById(attachments, fallbackMediaId))
    if (media?.type === 'image') return media.url
  }

  return undefined
}

function resolvePublisherJsonLd(seo: RecordLike | null): Record<string, unknown> | undefined {
  const publisher = asRecord(firstValue(seo, [['publisher']]))
  if (!publisher) return undefined

  const normalized = pruneJsonLd(publisher)

  return asRecord(normalized) ?? undefined
}

function pruneJsonLd(value: unknown): unknown {
  if (Array.isArray(value)) {
    const items = value
      .map(pruneJsonLd)
      .filter((item) => item != null)

    return items.length ? items : undefined
  }

  const record = asRecord(value)
  if (!record) {
    if (typeof value === 'string') {
      const trimmed = value.trim()
      return trimmed ? trimmed : undefined
    }

    return value ?? undefined
  }

  const next: Record<string, unknown> = {}

  for (const [key, item] of Object.entries(record)) {
    const normalized = pruneJsonLd(item)
    if (normalized != null) {
      next[key] = normalized
    }
  }

  return Object.keys(next).length ? next : undefined
}

function hydrateJsonLdNode(
  value: unknown,
  fallback: {
    canonical: string
    description: string
    title: string
  },
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => hydrateJsonLdNode(item, fallback))
  }

  const record = asRecord(value)
  if (!record) return value

  const type = normalizeText(record['@type'])
  const next: Record<string, unknown> = { ...record }

  if (!normalizeText(next.name)) {
    next.name = fallback.title
  }

  if (
    (type === 'WebPage' || type === 'MedicalWebPage' || type === 'Article')
    && !normalizeText(next.description)
  ) {
    next.description = fallback.description
  }

  if (type === 'WebPage' || type === 'MedicalWebPage' || type === 'Article') {
    if (!normalizeText(next.url)) {
      next.url = fallback.canonical
    }

    const mainEntityOfPage = asRecord(next.mainEntityOfPage)
    if (mainEntityOfPage && !normalizeText(mainEntityOfPage['@id'])) {
      next.mainEntityOfPage = {
        ...mainEntityOfPage,
        '@id': fallback.canonical,
      }
    }
  }

  return next
}

function resolveJsonLdFromSeo(
  seo: RecordLike | null,
  fallback: {
    canonical: string
    description: string
    title: string
  },
): unknown {
  const rawJsonLd = firstValue(seo, [
    ['jsonLd'],
    ['jsonld'],
    ['json_ld'],
  ])

  const hydrated = hydrateJsonLdNode(rawJsonLd, fallback)

  return pruneJsonLd(hydrated)
}

function buildPageJsonLd(options: {
  canonical: string
  description: string
  publisher?: Record<string, unknown>
  title: string
  type: SeoOgType
}) {
  return {
    '@context': 'https://schema.org',
    '@type': options.type === 'article' ? 'Article' : 'WebPage',
    '@id': `${options.canonical}#webpage`,
    url: options.canonical,
    name: options.title,
    headline: options.title,
    description: options.description,
    ...(options.publisher ? { publisher: options.publisher } : {}),
  }
}

function isTrainerContent(content?: SeoSource | null) {
  return content?.type === 'trainer' || content?.type === 'trainers'
}

function resolveTrainerDescription(
  content: SeoSource,
  fallbackDescription: string,
) {
  return firstString(asRecord(content.raw), [
    ['acf', 'excerpt', 'excerpt_text'],
    ['acf', 'excerpt', 'super_title'],
  ]) || firstString(asRecord(content.excerpt), [
    ['excerpt_text'],
    ['text'],
    ['description'],
  ]) || fallbackDescription
}

function buildHealthClubName(siteName: string, locationName: string) {
  if (locationName.toLocaleLowerCase().startsWith(siteName.toLocaleLowerCase())) {
    return locationName
  }

  return `${siteName} ${locationName}`
}

function buildTrainerWorksFor(options: {
  content: SeoSource
  locations: SeoSource[]
  siteName: string
  siteOrigin?: string
}) {
  const trainerLocations = firstValue(asRecord(options.content.raw), [
    ['acf', 'person_details', 'details', 'locations'],
  ])

  if (!Array.isArray(trainerLocations)) return []

  const seen = new Set<string>()

  return trainerLocations.flatMap((value) => {
    const trainerLocation = asRecord(value)
    if (!trainerLocation) return []

    const slug = firstString(trainerLocation, [['slug']])
    const fallbackName = firstString(trainerLocation, [['name']])
    const location = options.locations.find((candidate) =>
      slug
        ? candidate.slug === slug
        : candidate.title === fallbackName,
    )
    const locationName = normalizeText(location?.title) || fallbackName
    const identity = slug || locationName

    if (!locationName || !identity || seen.has(identity)) return []
    seen.add(identity)

    const locationRaw = asRecord(location?.raw)
    const streetAddress = firstString(locationRaw, [
      ['acf', 'details', 'location_details', 'address_details', 'address'],
    ])
    const postalCode = firstString(locationRaw, [
      ['acf', 'details', 'location_details', 'address_details', 'postal_code'],
    ])
    const addressLocality = firstString(locationRaw, [
      ['acf', 'details', 'location_details', 'address_details', 'city'],
    ])
    const hasAddress = Boolean(streetAddress || postalCode || addressLocality)
    const url = location?.relativeUrl
      ? buildCanonicalUrl(location.relativeUrl, '/', options.siteOrigin)
      : buildAbsoluteUrl('/', options.siteOrigin)

    return [{
      '@type': 'HealthClub',
      name: buildHealthClubName(options.siteName, locationName),
      ...(url ? { url } : {}),
      ...(hasAddress
        ? {
            address: {
              '@type': 'PostalAddress',
              ...(streetAddress ? { streetAddress } : {}),
              ...(addressLocality ? { addressLocality } : {}),
              ...(postalCode ? { postalCode } : {}),
              addressCountry: 'NO',
            },
          }
        : {}),
    }]
  })
}

function buildTrainerJsonLd(options: {
  attachments: AttachmentModel[]
  canonical: string
  content: SeoSource
  description: string
  fallbackImage?: string
  locale?: string
  locations: SeoSource[]
  siteName: string
  siteOrigin?: string
  title: string
}) {
  const raw = asRecord(options.content.raw)
  const name = firstString(raw, [
    ['acf', 'person_details', 'details', 'name'],
  ]) || normalizeText(options.content.title) || options.title
  const image = imageUrlFromUnknown(firstValue(raw, [
    ['acf', 'person_details', 'image'],
  ]), options.attachments) || options.fallbackImage
  const worksFor = buildTrainerWorksFor({
    content: options.content,
    locations: options.locations,
    siteName: options.siteName,
    siteOrigin: options.siteOrigin,
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    jobTitle: normalizeHtmlLang(options.locale) === 'en'
      ? 'Personal Trainer'
      : 'Personlig Trener',
    description: resolveTrainerDescription(options.content, options.description),
    url: options.canonical,
    ...(image ? { image } : {}),
    ...(worksFor.length > 0 ? { worksFor } : {}),
  }
}

function resolveFallbackTitle(content?: SeoSource | null) {
  return firstString(asRecord(content?.hero), [
    ['title'],
  ])
}

function resolveHeroImage(
  content: SeoSource | null | undefined,
  attachments: AttachmentModel[],
) {
  const hero = asRecord(content?.hero)

  if (!hero) return undefined

  return imageUrlFromUnknown(firstValue(hero, [
    ['media', 'image_url'],
    ['media', 'image_id'],
    ['media'],
  ]), attachments)
}

function resolveFallbackDescription(content?: SeoSource | null) {
  return (
    firstString(asRecord(content?.hero), [
      ['introContent'],
      ['intro_content'],
      ['intro_content_html'],
      ['content_html'],
      ['content'],
    ])
  )
}

export function buildSeoHead(options: BuildSeoHeadOptions): Head {
  const {
    attachments = [],
    content,
    defaults,
    locations = [],
    locale,
    routePath,
    siteName,
    siteOrigin,
  } = options

  const htmlLang = normalizeHtmlLang(locale)
  const ogLocale = normalizeOgLocale(locale)
  const seo = asRecord(content?.seo)

  const title =
    firstString(seo, [
      ['title'],
    ]) ||
    resolveFallbackTitle(content) ||
    normalizeText(content?.title) ||
    defaults.title

  const description =
    firstString(seo, [
      ['description'],
    ]) ||
    resolveFallbackDescription(content) ||
    defaults.description

  // The route is the frontend's source of truth. CMS canonical fields and
  // relative URLs can still point at the reusable WordPress source site.
  const canonical = buildCanonicalUrl(routePath, routePath, siteOrigin)

  const ogTitle =
    firstString(seo, [
      ['og', 'title'],
    ]) || title

  const ogDescription =
    firstString(seo, [
      ['og', 'description'],
    ]) || description

  const ogImage =
    resolveImage(seo, attachments, content?.media, [
      ['og', 'image'],
    ]) ||
    resolveHeroImage(content, attachments) ||
    defaults.image

  const ogUrl = canonical

  const twitterTitle =
    firstString(seo, [
      ['twitter', 'title'],
    ]) || ogTitle

  const twitterDescription =
    firstString(seo, [
      ['twitter', 'description'],
    ]) || ogDescription

  const twitterImage =
    resolveImage(seo, attachments, content?.media, [
      ['twitter', 'image'],
    ]) || ogImage

  const twitterCard =
    firstString(seo, [
      ['twitter', 'card'],
    ]) || (twitterImage ? 'summary_large_image' : 'summary')

  const twitterSite = firstString(seo, [
    ['twitter', 'site'],
  ])

  const robots = firstString(seo, [
    ['robots'],
  ])

  const ogType = normalizeOgType(
    firstString(seo, [
      ['og', 'type'],
    ]),
    content?.type === 'post' ? 'article' : 'website',
  )

  const jsonLd = resolveJsonLdFromSeo(seo, {
    canonical,
    description,
    title,
  }) ?? (
    content && isTrainerContent(content)
      ? buildTrainerJsonLd({
          attachments,
          canonical,
          content,
          description,
          fallbackImage: ogImage,
          locale,
          locations,
          siteName,
          siteOrigin,
          title,
        })
      : buildPageJsonLd({
          canonical,
          description,
          publisher: resolvePublisherJsonLd(seo),
          title,
          type: ogType,
        })
  )
  const alternateLinks = resolveAlternateLanguageLinks(content?.translations, siteOrigin)

  const resolvedSiteName =
    firstString(seo, [
      ['og', 'site_name'],
      ['twitter', 'site'],
    ]) || siteName

  const headMeta: NonNullable<Head['meta']> = [
    { name: 'description', content: description },
    { property: 'og:type', content: ogType },
    { property: 'og:url', content: ogUrl },
    { property: 'og:title', content: ogTitle },
    { property: 'og:description', content: ogDescription },
    { property: 'og:site_name', content: resolvedSiteName },
    { property: 'og:locale', content: ogLocale },
    { name: 'twitter:card', content: twitterCard },
    { name: 'twitter:url', content: canonical },
    { name: 'twitter:title', content: twitterTitle },
    { name: 'twitter:description', content: twitterDescription },
  ]

  if (ogImage) {
    headMeta.push({ property: 'og:image', content: ogImage })
  }

  if (twitterImage) {
    headMeta.push({ name: 'twitter:image', content: twitterImage })
  }

  if (twitterSite) {
    headMeta.push({ name: 'twitter:site', content: twitterSite })
  }

  if (robots) {
    headMeta.push({ name: 'robots', content: robots })
  }

  return {
    htmlAttrs: {
      lang: htmlLang,
    },
    title,
    link: [
      {
        rel: 'canonical',
        key: 'canonical',
        href: canonical,
      },
      ...alternateLinks,
    ],
    meta: headMeta,
    script: jsonLd
      ? [
          {
            key: 'route-jsonld',
            id: 'route-jsonld',
            type: 'application/ld+json',
            textContent: JSON.stringify(jsonLd),
          },
        ]
      : [],
  }
}

export default buildSeoHead
